import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  TrendingUp,
  Description,
  AccessTime,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SmartNotifications from '../../components/SmartNotifications/SmartNotifications';
import SmartTasks from '../../components/TaskManager/SmartTasks';

// Données de démonstration pour le graphique
const mockChartData = [
  { name: 'Jan', value: 12 },
  { name: 'Fév', value: 19 },
  { name: 'Mar', value: 15 },
  { name: 'Avr', value: 25 },
  { name: 'Mai', value: 22 },
  { name: 'Jun', value: 30 },
];

// Configuration des widgets
const widgetConfigs = [
  {
    id: 'progress',
    title: 'Progression Globale',
    color: '#2196f3',
    icon: TrendingUp,
    value: 75,
  },
  {
    id: 'documents',
    title: 'Documents en Attente',
    color: '#ff9800',
    icon: Description,
    value: 8,
  },
  {
    id: 'time',
    title: 'Temps Estimé',
    color: '#4caf50',
    icon: AccessTime,
    value: '3j',
  },
  {
    id: 'alerts',
    title: 'Alertes',
    color: '#f44336',
    icon: Warning,
    value: 2,
  },
];

const SmartWidget = ({ config }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.02 }}
    >
      <Paper
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color="textSecondary">
            {config.title}
          </Typography>
          <IconButton size="small" onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={typeof config.value === 'number' ? config.value : 100}
              size={80}
              thickness={4}
              sx={{ color: config.color }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" component="div" color="textPrimary">
                {config.value}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={handleClose}>Détails</MenuItem>
          <MenuItem onClick={handleClose}>Personnaliser</MenuItem>
          <MenuItem onClick={handleClose}>Masquer</MenuItem>
        </Menu>
      </Paper>
    </motion.div>
  );
};

const PredictiveChart = () => {
  const theme = useTheme();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Paper
        sx={{
          p: 3,
          height: '100%',
          minHeight: 400,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Analyse Prédictive
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={mockChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="value"
              stroke={theme.palette.primary.main}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 8 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Paper>
    </motion.div>
  );
};

const SmartDashboard = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simuler le chargement des données
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Dashboard Intelligent
        </Typography>

        <Grid container spacing={3}>
          {widgetConfigs.map((config) => (
            <Grid item xs={12} sm={6} md={3} key={config.id}>
              <SmartWidget config={config} />
            </Grid>
          ))}
          
          <Grid item xs={12} md={8}>
            <PredictiveChart />
          </Grid>

          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircle />
                  <Typography variant="h6">
                    Recommandations IA
                  </Typography>
                </Box>
                <Typography sx={{ mt: 2 }}>
                  Basé sur votre activité récente, nous recommandons de :
                </Typography>
                <Box component="ul" sx={{ mt: 1 }}>
                  <Typography component="li">Compléter le formulaire de déclaration TVA</Typography>
                  <Typography component="li">Mettre à jour vos statuts avant le 15 du mois</Typography>
                  <Typography component="li">Planifier une consultation avec votre expert-comptable</Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12}>
            <SmartTasks />
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
};

export default SmartDashboard; 